<script setup lang="ts">
import type { AxiosError } from "axios"
import type { SlideshowConfig } from "@/models"
import { computed, ref, useTemplateRef, watch } from "vue"
import { useGenerateSlideshowVideo, useGetSlideshowVideo, useValidateSlideshowVideo } from "@/api"
import { Alert, Button, Drawer, Icon, NoData } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { NotFoundException } from "@/http"
import { SlideshowExportDrawerKey } from "@/symbols"
import { useToast } from "../ui/ToastReka/useToast"
import MusicPicker from "./MusicPicker.vue"

interface SlideshowExportDrawerProps {
  eventVaultId?: number
  slideshowConfig?: SlideshowConfig
  drawerKey?: symbol
}

const props = withDefaults(defineProps<SlideshowExportDrawerProps>(), {
  drawerKey: SlideshowExportDrawerKey,
})

type MusicChoice = "random" | "select" | "none"

const { t, te } = useTranslation()
const toast = useToast()

const { isActive: isSlideshowExportDrawerActive, toggle: toggleSlideshowExportDrawer } = useModal(props.drawerKey)

const sessionId = computed(() => props.slideshowConfig?.id)
const eventVaultId = computed(() => props.eventVaultId)

const musicChoice = ref<MusicChoice>("random")
const selectedTrackId = ref<number>()
const musicPicker = useTemplateRef("musicPicker")

const showRegenerateConfirm = ref(false)

const {
  data: validation,
  isLoading: isLoadingValidation,
  error: validationError,
  refetch: refetchValidation,
} = useValidateSlideshowVideo(sessionId, eventVaultId, { enabled: isSlideshowExportDrawerActive })

const {
  data: videoJob,
  isLoading: isLoadingVideo,
  refetch: refetchVideo,
} = useGetSlideshowVideo(sessionId, eventVaultId, { enabled: isSlideshowExportDrawerActive })

const { mutate: mutateGenerateVideo, isPending: isGenerating } = useGenerateSlideshowVideo()

const isLoading = computed(() => isLoadingValidation.value || isLoadingVideo.value)
const isUnavailable = computed(() => validationError.value instanceof NotFoundException)

const videoState = computed<"generating" | "ready" | "failed" | "initial">(() => {
  if (videoJob.value?.isInProgress)
    return "generating"

  if (videoJob.value?.isCompleted && videoJob.value?.downloadUrl)
    return "ready"

  if (videoJob.value?.isFailed)
    return "failed"

  return "initial"
})

const canGenerate = computed(() =>
  Boolean(sessionId.value)
  && Boolean(eventVaultId.value)
  && !isGenerating.value
  && videoState.value !== "generating"
  && (validation.value?.isValid ?? false)
  && (musicChoice.value !== "select" || Boolean(selectedTrackId.value)),
)

const generateLabel = computed(() => {
  if (videoState.value === "ready")
    return t("slideshow_export_drawer.regenerate")

  if (videoState.value === "failed")
    return t("slideshow_export_drawer.try_again")

  return t("slideshow_export_drawer.generate")
})

const messageValues = computed(() => ({
  minMediaCount: validation.value?.minMediaCount ?? 0,
  maxMediaCount: validation.value?.maxMediaCount ?? 0,
  maxDurationSeconds: validation.value?.maxDurationSeconds ?? 0,
  currentMediaCount: validation.value?.currentMediaCount ?? 0,
  currentDurationSeconds: validation.value?.currentDurationSeconds ?? 0,
  currentTitleSlideDurationSeconds: validation.value?.currentTitleSlideDurationSeconds ?? 0,
  estimatedTotalDurationSeconds: validation.value?.estimatedTotalDurationSeconds ?? 0,
  estimatedTotalDurationMinutes: Math.ceil((validation.value?.estimatedTotalDurationSeconds ?? 0) / 60),
  maxTotalDurationMinutes: Math.floor((validation.value?.maxTotalDurationSeconds ?? 0) / 60),
}))

function hasMessage(key: string) {
  return te(key) || te(key, "en-US")
}

function warningMessage(code: string) {
  const key = `slideshow_export_drawer.warnings.${code}`

  return hasMessage(key) ? t(key, messageValues.value) : t("slideshow_export_drawer.warnings.UNKNOWN", { code })
}

function errorMessage(code: string) {
  const key = `slideshow_export_drawer.errors.${code}`

  return hasMessage(key) ? t(key, messageValues.value) : t("slideshow_export_drawer.errors.UNKNOWN", { code })
}

const blockingErrorMessage = computed(() => {
  const code = validation.value?.errorCode

  if (!code)
    return ""

  if (code === "SLIDESHOW_VIDEO_JOB_ALREADY_IN_PROGRESS" && videoState.value === "generating")
    return ""

  return errorMessage(code)
})

const showFooterActions = computed(() =>
  Boolean(props.slideshowConfig)
  && !isLoading.value
  && !isUnavailable.value
  && !validationError.value
  && (!blockingErrorMessage.value || (videoState.value === "ready" && Boolean(videoJob.value?.downloadUrl))),
)
function errorCodeFrom(error: unknown) {
  return (error as AxiosError<{ errorCode?: string }>)?.response?.data?.errorCode
}

function onGenerateClick() {
  if (videoState.value === "ready" && !showRegenerateConfirm.value) {
    showRegenerateConfirm.value = true
    return
  }

  onGenerate()
}

function onGenerate() {
  if (!sessionId.value || !eventVaultId.value) {
    toast.error()
    return
  }

  musicPicker.value?.stopPreview()

  mutateGenerateVideo({
    id: sessionId.value,
    eventVaultId: eventVaultId.value,
    musicAssetId: musicChoice.value === "select" ? selectedTrackId.value : undefined,
    skipBackgroundMusic: musicChoice.value === "none",
  }, {
    onSuccess: () => {
      showRegenerateConfirm.value = false
      toast.success({ description: t("slideshow_export_drawer.generate_success") })
      refetchVideo()
    },
    onError: (error) => {
      const code = errorCodeFrom(error)

      toast.error(code ? { description: errorMessage(code) } : {})
      refetchVideo()
      refetchValidation()
    },
  })
}

// Hide regenerate confirm when the drawer closes.
watch(isSlideshowExportDrawerActive, (active) => {
  if (!active)
    showRegenerateConfirm.value = false
})

// Re-validate when a job finishes.
watch(videoState, (state, previous) => {
  if (previous === "generating" && state !== "generating")
    refetchValidation()
})

// Hide regenerate confirm when switching slideshows.
watch(sessionId, () => {
  showRegenerateConfirm.value = false
})
</script>

<template>
  <Drawer :name="drawerKey" :title="t('slideshow_export_drawer.title')">
    <div v-if="isLoading" class="flex flex-col gap-5">
      <div class="gpx-skeleton w-full h-16" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-24" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-1/3 h-12" />
    </div>
    <div v-else-if="slideshowConfig" class="flex flex-col gap-5 pb-8 md:pb-6" data-test="slideshow-export-drawer">
      <div class="flex items-center gap-4">
        <span class="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon icon="Video" />
        </span>
        <h3 class="text-lg font-heading font-bold" data-test="slideshow-video-name">
          {{ slideshowConfig.name ? slideshowConfig.name : "—" }}
        </h3>
      </div>

      <Alert v-if="isUnavailable" variant="danger" :closable="false" data-test="slideshow-video-unavailable">
        {{ t("slideshow_export_drawer.not_available") }}
      </Alert>

      <div v-else-if="validationError" class="flex flex-col gap-4">
        <Alert variant="danger" :closable="false">
          {{ t("slideshow_export_drawer.load_error") }}
        </Alert>
        <Button variant="tertiary" class="self-start" @click="refetchValidation">
          {{ t("slideshow_export_drawer.try_again") }}
        </Button>
      </div>

      <template v-else>
        <p>{{ t("slideshow_export_drawer.description") }}</p>

        <Alert
          v-if="videoState === 'generating'"
          :closable="false"
          data-test="slideshow-video-generating"
        >
          <template #prefixIcon>
            <Icon icon="Loading" />
          </template>
          {{ t("slideshow_export_drawer.generating_message") }}
        </Alert>

        <Alert
          v-else-if="videoState === 'failed'"
          variant="danger"
          prefix-icon="Caution"
          :closable="false"
          data-test="slideshow-video-failed"
        >
          {{ t("slideshow_export_drawer.failed_message") }}
        </Alert>

        <Alert
          v-if="blockingErrorMessage"
          variant="danger"
          prefix-icon="Caution"
          :closable="false"
          data-test="slideshow-video-error"
        >
          <span class="wrap-anywhere">{{ blockingErrorMessage }}</span>
        </Alert>

        <Alert
          v-if="validation?.warnings.length"
          prefix-icon="CircleInfo"
          :closable="false"
          data-test="slideshow-video-warnings"
        >
          <ul class="flex flex-col gap-1.5" :class="{ 'list-disc pl-4': validation.warnings.length > 1 }">
            <li v-for="warning in validation.warnings" :key="warning.code" class="wrap-anywhere">
              {{ warningMessage(warning.code) }}
            </li>
          </ul>
        </Alert>

        <MusicPicker
          ref="musicPicker"
          v-model:choice="musicChoice"
          v-model:track-id="selectedTrackId"
          :event-vault-id="eventVaultId"
          :enabled="isSlideshowExportDrawerActive"
          :hidden="videoState === 'generating' || Boolean(blockingErrorMessage)"
          :reset-key="sessionId"
        />

        <Alert
          v-if="showRegenerateConfirm"
          variant="danger"
          prefix-icon="Caution"
          :closable="false"
          data-test="regenerate-confirm"
        >
          <div class="flex flex-col items-start gap-3">
            <p>{{ t("slideshow_export_drawer.replace_warning") }}</p>
            <div class="flex gap-3">
              <Button
                variant="danger"
                size="sm"
                :disabled="!canGenerate"
                data-test="regenerate-confirm-button"
                @click="onGenerateClick"
              >
                {{ t("slideshow_export_drawer.replace_confirm") }}
              </Button>
              <Button
                variant="tertiary"
                size="sm"
                data-test="regenerate-cancel-button"
                @click="showRegenerateConfirm = false"
              >
                {{ t("cancel") }}
              </Button>
            </div>
          </div>
        </Alert>
      </template>
    </div>
    <NoData v-else :button-label="t('close')" @click="toggleSlideshowExportDrawer" />

    <template #footer>
      <div
        v-if="showFooterActions"
        class="flex gap-4 px-6 py-4 bg-white border-t border-slate-200"
      >
        <Button
          v-if="!blockingErrorMessage"
          :disabled="!canGenerate || showRegenerateConfirm"
          class="flex-1"
          data-test="generate-video-button"
          @click="onGenerateClick"
        >
          {{ generateLabel }}
        </Button>
        <Button
          v-if="videoState === 'ready' && videoJob?.downloadUrl"
          :href="videoJob.downloadUrl"
          class="flex-1"
          variant="secondary"
          data-test="download-video-button"
          @click.stop
        >
          {{ t("download") }}
        </Button>
      </div>
    </template>
  </Drawer>
</template>
